/**
 * Marketing packages: browse the catalogue, request one, track its content plan.
 *
 * Replaces `renderDoctorPackages`, `fetchDoctorPackageRequests`,
 * `requestPackage`, `openPackageDetailsModal` (defined twice),
 * `saveDoctorTopicAndSync`, `confirmTopicEdit`, `updateDoctorPostTopic`
 * (twice), `updateDoctorReelTopic`, `updateDoctorHourTopic`,
 * `toggleDoctorTopicVisibility` (twice) and `calculateSuggestedDates`.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Icon,
  Input,
  Modal,
  PageHeader,
  Panel,
  SectionTitle,
  Spinner,
  Tabs,
} from '../../components/common';
import { useNotifications } from '../../context/NotificationContext';
import { useData } from '../../context/DataContext';
import { useAsync } from '../../hooks/useAsync';
import { doctorApi } from '../../api/endpoints';
import { formatCurrency } from '../../domain/pricing';
import { isOfferValid } from '../../domain/offers';
import { addDays, isoToDisplayDate, todayKey } from '../../domain/dates';

const PACKAGE_TYPE_LABELS = {
  posts: 'بوستات',
  reels: 'ريلز',
  combo: 'مكس',
  photography: 'تصوير',
};

const REQUEST_STATUS_LABELS = {
  pending: 'قيد المراجعة',
  approved: 'تمت الموافقة',
  confirmed: 'مؤكدة',
  active: 'جارية',
  completed: 'مكتملة',
  rejected: 'مرفوضة',
  expired: 'منتهية',
};

const REQUEST_STATUS_STYLES = {
  pending: 'border-amber-500/30 bg-amber-500/10 text-amber-300',
  approved: 'border-cyan-500/30 bg-cyan-500/10 text-cyan-300',
  confirmed: 'border-cyan-500/30 bg-cyan-500/10 text-cyan-300',
  active: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300',
  completed: 'border-slate-600 bg-slate-800 text-slate-300',
  rejected: 'border-red-500/30 bg-red-500/10 text-red-300',
  expired: 'border-slate-600 bg-slate-800 text-slate-400',
};

const TABS = [
  { id: 'catalogue', label: 'الباقات المتاحة', icon: 'package' },
  { id: 'requests', label: 'طلباتي', icon: 'clipboard-list' },
];

/** Effective price after a package's own promotional offer. */
function packagePrice(pkg) {
  const base = Number(pkg.price) || 0;
  const offer = pkg.offerPercent
    ? { percent: pkg.offerPercent, expiry: pkg.offerExpiry, name: pkg.offerName }
    : null;
  if (!offer || !isOfferValid(offer)) return { base, final: base, offer: null };
  return { base, final: Math.round(base - base * (offer.percent / 100)), offer };
}

export function DoctorPackages() {
  const { packages, packagesState } = useData();
  const { success, error: toastError } = useNotifications();

  const requestsState = useAsync(() => doctorApi.packageRequests(), [], { initialData: [] });
  const requests = requestsState.data || [];

  const [tab, setTab] = useState('catalogue');
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [detailsTarget, setDetailsTarget] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleRequest = async () => {
    if (!confirmTarget) return;
    setSubmitting(true);
    try {
      const { final } = packagePrice(confirmTarget);
      await doctorApi.requestPackage(confirmTarget.id, final);
      success('تم إرسال طلب الباقة — بانتظار موافقة الإدارة');
      setConfirmTarget(null);
      setTab('requests');
      requestsState.reload();
    } catch (err) {
      toastError(err.message || 'تعذر إرسال الطلب');
    } finally {
      setSubmitting(false);
    }
  };

  const tabsWithCounts = TABS.map((t) => ({
    ...t,
    count: t.id === 'catalogue' ? packages.length : requests.length,
  }));

  return (
    <div className="space-y-6">
      <PageHeader
        title="باقاتي التسويقية"
        subtitle="اشترك في باقات المحتوى وتابع خطة النشر"
        icon="package"
      />

      <Tabs tabs={tabsWithCounts} active={tab} onChange={setTab} />

      {tab === 'catalogue' && (
        <>
          {packagesState.error ? (
            <ErrorState error={packagesState.error} />
          ) : packagesState.loading && packages.length === 0 ? (
            <Spinner variant="inline" label="جاري تحميل الباقات..." />
          ) : packages.length === 0 ? (
            <Panel>
              <EmptyState icon="package-x" title="لا توجد باقات متاحة حالياً" />
            </Panel>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {packages.map((pkg) => (
                <PackageCard key={pkg.id} pkg={pkg} onRequest={() => setConfirmTarget(pkg)} />
              ))}
            </div>
          )}
        </>
      )}

      {tab === 'requests' && (
        <>
          {requestsState.error ? (
            <ErrorState error={requestsState.error} onRetry={requestsState.reload} />
          ) : requestsState.loading && requests.length === 0 ? (
            <Spinner variant="inline" label="جاري تحميل طلباتك..." />
          ) : requests.length === 0 ? (
            <Panel>
              <EmptyState
                icon="clipboard-x"
                title="لا توجد طلبات"
                message="اختر باقة من التبويب الأول لإرسال طلبك الأول."
                action={<Button onClick={() => setTab('catalogue')}>تصفح الباقات</Button>}
              />
            </Panel>
          ) : (
            <div className="space-y-3">
              {requests.map((request) => (
                <RequestCard
                  key={request.id}
                  request={request}
                  onOpen={() => setDetailsTarget(request)}
                />
              ))}
            </div>
          )}
        </>
      )}

      <ConfirmDialog
        open={Boolean(confirmTarget)}
        onClose={() => setConfirmTarget(null)}
        onConfirm={handleRequest}
        loading={submitting}
        title="تأكيد طلب الباقة"
        confirmLabel="إرسال الطلب"
        message={
          confirmTarget
            ? `سيتم إرسال طلب للاشتراك في "${confirmTarget.name}" بقيمة ${formatCurrency(
                packagePrice(confirmTarget).final,
              )}.\n\nسيراجع فريق الإدارة الطلب ويتواصل معك لترتيب الدفع.`
            : ''
        }
      />

      <PackageDetailsModal
        request={detailsTarget}
        onClose={() => setDetailsTarget(null)}
        onSaved={() => {
          requestsState.reload();
          success('تم حفظ خطة المحتوى');
        }}
        onError={toastError}
      />
    </div>
  );
}

/* ========================================================================== */

function PackageCard({ pkg, onRequest }) {
  const { base, final, offer } = packagePrice(pkg);

  return (
    <Panel className="hover-card flex flex-col gap-4">
      <div className="flex items-start gap-3">
        <span className="rounded-xl bg-purple-500/10 p-3 text-purple-400">
          <Icon name="package" size={22} />
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-base font-bold text-white">{pkg.name}</h3>
          <Badge className="mt-1 border-slate-600 bg-slate-800 text-slate-300">
            {PACKAGE_TYPE_LABELS[pkg.type] || pkg.type}
          </Badge>
        </div>
      </div>

      <ul className="space-y-1.5 text-xs text-slate-300">
        <li className="flex items-center gap-2">
          <Icon name="check" size={13} className="text-emerald-400" />
          {pkg.quantity} {PACKAGE_TYPE_LABELS[pkg.type] || 'عنصر'}
        </li>
        {pkg.reelQuantity > 0 && (
          <li className="flex items-center gap-2">
            <Icon name="check" size={13} className="text-emerald-400" />
            {pkg.reelQuantity} ريل
          </li>
        )}
      </ul>

      <div className="mt-auto border-t border-white/5 pt-3">
        {offer && (
          <div className="mb-1 flex items-center justify-between">
            <Badge className="border-emerald-500/30 bg-emerald-500/10 text-emerald-300" icon="tag">
              {offer.name || 'عرض'} — {offer.percent}%
            </Badge>
            <span className="text-xs text-slate-500 line-through">{formatCurrency(base)}</span>
          </div>
        )}

        <div className="flex items-center justify-between gap-2">
          <span className="text-lg font-bold text-emerald-400">{formatCurrency(final)}</span>
          <Button icon="send" onClick={onRequest}>
            اطلب الباقة
          </Button>
        </div>
      </div>
    </Panel>
  );
}

/* ========================================================================== */

function RequestCard({ request, onOpen }) {
  const status = request.status || 'pending';
  const pkg = request.Package || {};

  return (
    <Panel className="flex flex-wrap items-center gap-4">
      <span className="rounded-xl bg-purple-500/10 p-3 text-purple-400">
        <Icon name="package" size={20} />
      </span>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="truncate font-bold text-white">{pkg.name || `طلب #${request.id}`}</h3>
          <Badge className={REQUEST_STATUS_STYLES[status] || REQUEST_STATUS_STYLES.pending}>
            {REQUEST_STATUS_LABELS[status] || status}
          </Badge>
          {request.paymentConfirmed && (
            <Badge className="border-emerald-500/30 bg-emerald-500/10 text-emerald-300" icon="check">
              مدفوعة
            </Badge>
          )}
        </div>

        <p className="mt-1 flex flex-wrap items-center gap-x-3 text-xs text-slate-400">
          <span>{formatCurrency(request.amount_paid || request.price)}</span>
          {request.requestDate && (
            <span>{isoToDisplayDate(String(request.requestDate).slice(0, 10))}</span>
          )}
          {(request.AssignedTeamMembers || []).length > 0 && (
            <span className="flex items-center gap-1">
              <Icon name="users" size={12} />
              {request.AssignedTeamMembers.map((m) => m.name).join('، ')}
            </span>
          )}
        </p>
      </div>

      <Button variant="ghost" icon="list-checks" onClick={onOpen}>
        خطة المحتوى
      </Button>
    </Panel>
  );
}

/* ========================================================================== */

/**
 * Content plan editor.
 *
 * Topics are stored inside `PackageRequest.contentDetails` as parallel arrays
 * keyed by content type (`postsTopics`, `reelsTopics`, …) — an awkward shape,
 * but it is what PATCH /api/doctors/package-requests/:id/topics merges into, and
 * what the admin and team views read.
 */
function PackageDetailsModal({ request, onClose, onSaved, onError }) {
  const pkg = request?.Package || {};
  const details = request?.contentDetails || {};

  const postCount = Number(pkg.quantity) || 0;
  const reelCount = Number(pkg.reelQuantity) || 0;

  const [postTopics, setPostTopics] = useState([]);
  const [reelTopics, setReelTopics] = useState([]);
  const [saving, setSaving] = useState(false);

  // Seed the editors whenever a different request is opened.
  useEffect(() => {
    if (!request) return;
    setPostTopics(Array.from({ length: postCount }, (_, i) => details.postsTopics?.[i] || ''));
    setReelTopics(Array.from({ length: reelCount }, (_, i) => details.reelsTopics?.[i] || ''));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request?.id]);

  /** Evenly spread the pieces over the coming weeks, as `calculateSuggestedDates` did. */
  const suggestedDates = useMemo(() => {
    const total = postCount + reelCount;
    if (total === 0) return [];
    const spacing = Math.max(1, Math.floor(30 / total));
    return Array.from({ length: total }, (_, i) => addDays(todayKey(), (i + 1) * spacing));
  }, [postCount, reelCount]);

  if (!request) return null;

  const handleSave = async () => {
    setSaving(true);
    try {
      await doctorApi.updatePackageTopics(request.id, {
        postsTopics: postTopics,
        reelsTopics: reelTopics,
        postsTopicsConfirmed: postTopics.map((t) => Boolean(String(t).trim())),
        reelsTopicsConfirmed: reelTopics.map((t) => Boolean(String(t).trim())),
      });
      onSaved();
      onClose();
    } catch (err) {
      onError(err.message || 'تعذر حفظ خطة المحتوى');
    } finally {
      setSaving(false);
    }
  };

  const filled = [...postTopics, ...reelTopics].filter((t) => String(t).trim()).length;
  const total = postCount + reelCount;

  return (
    <Modal
      open
      onClose={onClose}
      title="خطة المحتوى"
      icon="list-checks"
      size="lg"
      footer={
        <div className="flex items-center justify-between gap-3">
          <span className="text-xs text-slate-400">
            {filled} من {total} موضوع
          </span>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose}>
              إغلاق
            </Button>
            <Button icon="save" loading={saving} onClick={handleSave}>
              حفظ الخطة
            </Button>
          </div>
        </div>
      }
    >
      <div className="space-y-6">
        <div className="rounded-xl border border-white/5 bg-white/5 p-3">
          <h3 className="font-bold text-white">{pkg.name}</h3>
          <p className="mt-0.5 text-xs text-slate-400">
            {postCount > 0 && `${postCount} بوست`}
            {postCount > 0 && reelCount > 0 && ' — '}
            {reelCount > 0 && `${reelCount} ريل`}
          </p>
        </div>

        {total === 0 ? (
          <EmptyState
            icon="file-question"
            title="لا يوجد محتوى قابل للتخطيط"
            message="هذه الباقة لا تتضمن بوستات أو ريلز."
          />
        ) : (
          <>
            {postCount > 0 && (
              <section>
                <SectionTitle icon="image">مواضيع البوستات</SectionTitle>
                <div className="space-y-2">
                  {postTopics.map((topic, index) => (
                    <TopicRow
                      key={`post-${index}`}
                      index={index}
                      value={topic}
                      suggestedDate={suggestedDates[index]}
                      onChange={(value) =>
                        setPostTopics((current) =>
                          current.map((t, i) => (i === index ? value : t)),
                        )
                      }
                    />
                  ))}
                </div>
              </section>
            )}

            {reelCount > 0 && (
              <section>
                <SectionTitle icon="video">مواضيع الريلز</SectionTitle>
                <div className="space-y-2">
                  {reelTopics.map((topic, index) => (
                    <TopicRow
                      key={`reel-${index}`}
                      index={index}
                      value={topic}
                      suggestedDate={suggestedDates[postCount + index]}
                      onChange={(value) =>
                        setReelTopics((current) =>
                          current.map((t, i) => (i === index ? value : t)),
                        )
                      }
                    />
                  ))}
                </div>
              </section>
            )}

            <p className="rounded-lg border border-cyan-500/20 bg-cyan-500/10 p-2.5 text-[11px] leading-relaxed text-cyan-200">
              <Icon name="info" size={12} className="me-1 inline" />
              التواريخ المقترحة موزعة على الشهر القادم. يمكن لفريق التسويق تعديلها بعد الاعتماد.
            </p>
          </>
        )}
      </div>
    </Modal>
  );
}

function TopicRow({ index, value, suggestedDate, onChange }) {
  return (
    <div className="flex items-center gap-2">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-800 text-xs font-bold text-slate-400">
        {index + 1}
      </span>
      <Input
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={`موضوع رقم ${index + 1}`}
        className="flex-1"
      />
      {suggestedDate && (
        <span className="hidden shrink-0 rounded-lg bg-white/5 px-2 py-1.5 text-[10px] text-slate-400 sm:block">
          {isoToDisplayDate(suggestedDate)}
        </span>
      )}
    </div>
  );
}

export default DoctorPackages;
